import React from 'react';
import { Box, Typography, Button, Alert, Container, Paper } from '@mui/material';
import { Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';
import { useRouteError, useNavigate } from 'react-router-dom';

interface RouteError {
  status?: number;
  statusText?: string;
  message?: string;
  data?: unknown;
}

export const ErrorBoundary: React.FC = () => {
  const error = useRouteError() as RouteError;
  const navigate = useNavigate();

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const getErrorMessage = (): string => {
    if (error?.status === 404) {
      return 'The page you are looking for does not exist.';
    }

    if (error?.status === 500) {
      return 'An internal server error occurred.';
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.statusText) {
      return error.statusText;
    }

    return 'An unexpected error occurred.';
  };

  const getErrorTitle = (): string => {
    if (error?.status === 404) {
      return 'Page Not Found';
    }

    if (error?.status === 500) {
      return 'Server Error';
    }

    return 'Application Error';
  };

  return (
    <Container maxWidth={false}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            width: '100%',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="error">
            {getErrorTitle()}
          </Typography>

          {error?.status && (
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Error {error.status}
            </Typography>
          )}

          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {getErrorMessage()}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Refresh Page
            </Button>

            <Button variant="outlined" startIcon={<HomeIcon />} onClick={handleGoHome}>
              Go Home
            </Button>
          </Box>

          {/* Development error details */}
          {import.meta.env?.DEV && Boolean(error?.data) && (
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="h6" gutterBottom>
                Error Details (Development)
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: 'grey.100',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(error.data, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};
